import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { ArrowLeftIcon, ArrowRightIcon, BuildingIcon, Loader2Icon, RocketIcon, SettingsIcon, CompassIcon } from "lucide-react";
import { usePermissions } from "../../../PermissionsProvider";
import { CompassService, CreateCompassSearchRequest } from "../../../services/CompassService";
import { ApplicationService } from "../../../services/ApplicationService";
import { CompanyService } from "../../../services/CompanyService";
import { CompositionService } from "../../../services/CompositionService";
import { FinetuningService } from "../../../services/FinetuningService";
import { ApplicationAction } from "../../../model_gen/application";
import AutoTestStep from "../../Finetune/FinetuneRun/Steps/AutoTestStep";
import ReviewField from "../../Finetune/FinetuneRun/Steps/ReviewField";
import ReviewBenchmarkTestsCard from "../../Finetune/FinetuneRun/Steps/ReviewBenchmarkTestsCard";
import { Card, CardContent, CardHeader, CardTitle } from "../../ui/card";
import { Badge } from "../../ui/badge";
import { Button } from "../../ui/button";
import { Label } from "../../ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../../ui/select";
import { Slider } from "../../ui/slider";

interface CompanyOption {
  id: number;
  company_name: string;
}

interface AppOption {
  id: number;
  application_name?: string;
  name?: string;
}

interface CompositionOption {
  id: number;
  name: string;
}

const TRAINING_METHODS = ["SFT", "DPO", "GRPO"];
const STEPS = ["Source & Training", "Search & Benchmarks", "Review & Launch"];
const INTERNAL_COMPANY_IDS = [2, 51];

const NewCompassSearch: React.FC = () => {
  const navigate = useNavigate();
  const { apiKey, companyId: ownCompanyId } = usePermissions();
  const isInternal = INTERNAL_COMPANY_IDS.includes(ownCompanyId as number);
  const [step, setStep] = useState(0);
  const [submitting, setSubmitting] = useState(false);

  const [companies, setCompanies] = useState<CompanyOption[]>([]);
  const [selectedCompanyId, setSelectedCompanyId] = useState<string>("");
  const [applications, setApplications] = useState<AppOption[]>([]);
  const [intents, setIntents] = useState<ApplicationAction[]>([]);
  const [loadingIntents, setLoadingIntents] = useState(false);
  const [compositions, setCompositions] = useState<CompositionOption[]>([]);
  const [baseModels, setBaseModels] = useState<string[]>([]);

  const [sourceAppId, setSourceAppId] = useState<string>("");
  const [intentId, setIntentId] = useState<string>("");
  const [compositionId, setCompositionId] = useState<string>("");
  const [baseModel, setBaseModel] = useState<string>("");
  const [trainingMethod, setTrainingMethod] = useState<string>("SFT");
  const [accuracyThreshold, setAccuracyThreshold] = useState<number>(80);
  const [maxWaves, setMaxWaves] = useState<number>(3);
  const [runsPerWave, setRunsPerWave] = useState<number>(5);
  const [testSetIds, setTestSetIds] = useState<number[]>([]);
  const [evaluationCriteriaIds, setEvaluationCriteriaIds] = useState<number[]>([]);

  useEffect(() => {
    if (!apiKey) return;

    if (isInternal) {
      CompanyService.getAffiliatedCompanies(null, apiKey).subscribe(
        (data: any) => {
          const items = Array.isArray(data) ? data : [];
          setCompanies(items);
          if (ownCompanyId && !selectedCompanyId) {
            setSelectedCompanyId(String(ownCompanyId));
          }
        },
        (err: unknown) => console.error("Failed to load companies:", err)
      );
    } else if (ownCompanyId && !selectedCompanyId) {
      setSelectedCompanyId(String(ownCompanyId));
    }

    FinetuningService.getBaseModels(() => {}, apiKey).subscribe(
      (models: string[]) => {
        const unique = models.filter((m, i, arr) => arr.indexOf(m) === i);
        setBaseModels(unique);
        if (unique.length > 0 && !baseModel) setBaseModel(unique[0]);
      },
      (err: unknown) => console.error("Failed to load base models:", err)
    );
  }, [apiKey]);

  const resetSelections = () => {
    setSourceAppId("");
    setIntentId("");
    setCompositionId("");
    setTestSetIds([]);
    setEvaluationCriteriaIds([]);
  };

  useEffect(() => {
    if (!apiKey || !selectedCompanyId) {
      setApplications([]);
      setCompositions([]);
      resetSelections();
      return;
    }

    CompanyService.getApplicationsByCompany(selectedCompanyId, null, apiKey).subscribe(
      (apps: any[]) => {
        const items = Array.isArray(apps) ? apps : [];
        const appName = (a: any) => a.application_name || a.name || "";
        setApplications(items.filter((a: any) => appName(a) && !appName(a).startsWith("compass-")));
      },
      (err: unknown) => console.error("Failed to load applications:", err)
    );
  }, [apiKey, selectedCompanyId]);

  useEffect(() => {
    if (!apiKey || !sourceAppId) {
      setIntents([]);
      setIntentId("");
      setCompositions([]);
      setCompositionId("");
      return;
    }

    setLoadingIntents(true);
    ApplicationService.getApplicationActions(parseInt(sourceAppId), null, apiKey, selectedCompanyId || undefined).subscribe(
      (actions: ApplicationAction[]) => {
        setIntents(actions || []);
        setLoadingIntents(false);
      },
      (err: unknown) => {
        console.error("Failed to load intents:", err);
        setLoadingIntents(false);
      }
    );

    CompositionService.getCompositions(0, 200, () => {}, apiKey, undefined, true, selectedCompanyId || undefined).subscribe(
      (data: any) => {
        const items = Array.isArray(data?.items) ? data.items : Array.isArray(data) ? data : [];
        setCompositions(items);
      },
      (err: unknown) => console.error("Failed to load compositions:", err)
    );
  }, [apiKey, sourceAppId, selectedCompanyId]);

  const selectedCompany = companies.find((c) => c.id === parseInt(selectedCompanyId));
  const selectedApp = applications.find((a) => a.id === parseInt(sourceAppId));
  const selectedIntent = intents.find((i) => i.id === parseInt(intentId));
  const selectedComp = compositions.find((c) => c.id === parseInt(compositionId));

  const appDisplayName = (a: AppOption | undefined) => a?.application_name || a?.name || "";

  const canProceedStep0 = !!selectedCompanyId && !!sourceAppId && !!baseModel && !!trainingMethod && !!compositionId;

  const handleSubmit = () => {
    const payload: CreateCompassSearchRequest = {
      source_application_id: parseInt(sourceAppId),
      composition_id: parseInt(compositionId),
      base_model: baseModel,
      training_method: trainingMethod,
      accuracy_threshold: accuracyThreshold / 100,
      max_waves: maxWaves,
      runs_per_wave: runsPerWave,
    };

    if (intentId) payload.intent_id = parseInt(intentId);

    const cid = parseInt(selectedCompanyId);
    if (isInternal && cid && cid !== ownCompanyId) {
      payload.company_id = cid;
    }

    if (testSetIds.length > 0) payload.test_set_ids = testSetIds;
    if (evaluationCriteriaIds.length > 0) payload.evaluation_criteria_ids = evaluationCriteriaIds;

    CompassService.createSearch(payload, setSubmitting, apiKey).subscribe(
      (result: { id: number }) => {
        toast.success("Compass search created");
        navigate(`/admin/compass/${result.id}`);
      },
      (error: unknown) => {
        console.error("Failed to create search:", error);
        toast.error("Failed to create search");
        setSubmitting(false);
      }
    );
  };

  const goBack = () => {
    if (step === 0) navigate("/admin/compass");
    else setStep(step - 1);
  };

  return (
    <div className="p-6 max-w-2xl mx-auto space-y-6">
      <div className="flex items-center gap-3">
        <Button variant="ghost" size="sm" onClick={goBack}>
          <ArrowLeftIcon className="w-4 h-4 mr-1" />
          Back
        </Button>
        <h1 className="text-2xl font-gc-demi-bold tracking-tight">New Compass Search</h1>
      </div>

      <div className="flex items-center gap-2 mb-2">
        {STEPS.map((label, i) => (
          <React.Fragment key={label}>
            <div
              className={`flex items-center gap-2 px-3 py-1.5 rounded-full text-sm font-gc-med transition-colors ${
                i === step
                  ? "bg-primary text-primary-foreground"
                  : i < step
                  ? "bg-green-500/15 text-green-600 dark:text-green-400"
                  : "bg-muted text-muted-foreground"
              }`}
            >
              <span className="w-5 h-5 rounded-full bg-background/20 flex items-center justify-center text-xs">
                {i + 1}
              </span>
              {label}
            </div>
            {i < STEPS.length - 1 && <div className="h-px w-6 bg-border" />}
          </React.Fragment>
        ))}
      </div>

      {step === 0 && (
        <Card className="border-border">
          <CardHeader className="pb-4">
            <CardTitle className="text-lg font-gc-med text-foreground">Source & Training</CardTitle>
          </CardHeader>
          <CardContent className="space-y-5">
            {isInternal && (
              <div className="space-y-2">
                <Label className="text-sm font-gc-med">Company</Label>
                <Select value={selectedCompanyId} onValueChange={(v) => { setSelectedCompanyId(v); resetSelections(); }}>
                  <SelectTrigger>
                    <SelectValue placeholder="Select company" />
                  </SelectTrigger>
                  <SelectContent>
                    {companies.map((c) => (
                      <SelectItem key={c.id} value={String(c.id)}>
                        {c.company_name} (#{c.id})
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}

            <div className="space-y-2">
              <Label className="text-sm font-gc-med">Application</Label>
              <Select
                value={sourceAppId}
                onValueChange={(v) => { setSourceAppId(v); setIntentId(""); setCompositionId(""); setTestSetIds([]); setEvaluationCriteriaIds([]); }}
                disabled={!selectedCompanyId}
              >
                <SelectTrigger>
                  <SelectValue placeholder={selectedCompanyId ? "Select application" : "Select a company first"} />
                </SelectTrigger>
                <SelectContent>
                  {applications.map((app) => (
                    <SelectItem key={app.id} value={String(app.id)}>
                      {appDisplayName(app)}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <p className="text-xs text-muted-foreground font-gc-reg">
                The application to optimize. Runs will be isolated under a compass-* app.
              </p>
            </div>

            <div className="space-y-2">
              <Label className="text-sm font-gc-med">Intent</Label>
              <Select
                value={intentId}
                onValueChange={(v) => { setIntentId(v); setTestSetIds([]); setEvaluationCriteriaIds([]); }}
                disabled={!sourceAppId || loadingIntents}
              >
                <SelectTrigger>
                  <SelectValue placeholder={
                    loadingIntents ? "Loading..." :
                    !sourceAppId ? "Select an application first" :
                    "Select intent"
                  } />
                </SelectTrigger>
                <SelectContent>
                  {intents.map((intent) => (
                    <SelectItem key={intent.id} value={String(intent.id)}>
                      {intent.action_type}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label className="text-sm font-gc-med">Composition</Label>
              <Select value={compositionId} onValueChange={setCompositionId} disabled={!sourceAppId}>
                <SelectTrigger>
                  <SelectValue placeholder={!selectedCompanyId ? "Select a company first" : !sourceAppId ? "Select an application first" : "Select composition"} />
                </SelectTrigger>
                <SelectContent>
                  {compositions.map((c) => (
                    <SelectItem key={c.id} value={String(c.id)}>
                      {c.name} (#{c.id})
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label className="text-sm font-gc-med">Base Model</Label>
                <Select value={baseModel} onValueChange={setBaseModel}>
                  <SelectTrigger>
                    <SelectValue placeholder="Select base model" />
                  </SelectTrigger>
                  <SelectContent>
                    {baseModels.map((model) => (
                      <SelectItem key={model} value={model}>
                        {model}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <Label className="text-sm font-gc-med">Training Method</Label>
                <Select value={trainingMethod} onValueChange={setTrainingMethod}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {TRAINING_METHODS.map((m) => (
                      <SelectItem key={m} value={m}>
                        {m}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {step === 1 && (
        <>
          <Card className="border-border">
            <CardHeader className="pb-4">
              <CardTitle className="text-lg font-gc-med text-foreground">Search Settings</CardTitle>
            </CardHeader>
            <CardContent className="space-y-5">
              <div className="space-y-2">
                <Label className="text-sm font-gc-med">Accuracy Threshold: {accuracyThreshold}%</Label>
                <Slider
                  min={10}
                  max={100}
                  step={5}
                  value={[accuracyThreshold]}
                  onValueChange={([v]) => setAccuracyThreshold(v)}
                />
                <p className="text-xs text-muted-foreground font-gc-reg">
                  Target accuracy. Early stopping triggers when a run meets this threshold.
                </p>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label className="text-sm font-gc-med">Max Waves</Label>
                  <Select value={String(maxWaves)} onValueChange={(v) => setMaxWaves(parseInt(v))}>
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {[1, 2, 3, 4, 5].map((n) => (
                        <SelectItem key={n} value={String(n)}>
                          {n}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label className="text-sm font-gc-med">Runs per Wave</Label>
                  <Select value={String(runsPerWave)} onValueChange={(v) => setRunsPerWave(parseInt(v))}>
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {[3, 5, 7, 10].map((n) => (
                        <SelectItem key={n} value={String(n)}>
                          {n}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>
            </CardContent>
          </Card>

          <AutoTestStep
            selectedTestSetIds={testSetIds}
            setSelectedTestSetIds={setTestSetIds}
            selectedEvalCriteriaIds={evaluationCriteriaIds}
            setSelectedEvalCriteriaIds={setEvaluationCriteriaIds}
            intentGroupId={selectedIntent?.intent_group_id ?? undefined}
            applicationId={parseInt(sourceAppId)}
            intentId={selectedIntent?.id ?? undefined}
          />
        </>
      )}

      {step === 2 && (
        <div className="space-y-6">
          <div className="text-center space-y-3">
            <h2 className="text-2xl font-gc-demi-bold text-foreground">Review & Launch</h2>
            <p className="text-muted-foreground font-gc-reg">
              Confirm your configuration before launching the search.
            </p>
          </div>

          {isInternal && (
            <Card className="border-border">
              <CardHeader className="pb-4">
                <CardTitle className="flex items-center gap-2 text-lg font-gc-med text-foreground">
                  <BuildingIcon className="w-5 h-5 text-maitai-green" />
                  Company
                </CardTitle>
              </CardHeader>
              <CardContent>
                <ReviewField label="Company" value={selectedCompany ? `${selectedCompany.company_name} (#${selectedCompany.id})` : selectedCompanyId} />
              </CardContent>
            </Card>
          )}

          <Card className="border-border">
            <CardHeader className="pb-4">
              <CardTitle className="flex items-center gap-2 text-lg font-gc-med text-foreground">
                <CompassIcon className="w-5 h-5 text-maitai-green" />
                Source & Training
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                <ReviewField label="Application" value={appDisplayName(selectedApp) || sourceAppId} />
                {selectedIntent && (
                  <ReviewField label="Intent" value={selectedIntent.action_type} />
                )}
                <ReviewField label="Composition" value={selectedComp ? `${selectedComp.name} (#${selectedComp.id})` : compositionId} />
                <ReviewField label="Base Model" value={baseModel} />
                <ReviewField label="Training Method" value={<Badge variant="outline">{trainingMethod}</Badge>} />
              </div>
            </CardContent>
          </Card>

          <Card className="border-border">
            <CardHeader className="pb-4">
              <CardTitle className="flex items-center gap-2 text-lg font-gc-med text-foreground">
                <SettingsIcon className="w-5 h-5 text-maitai-green" />
                Search Settings
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                <ReviewField label="Accuracy Threshold" value={`${accuracyThreshold}%`} />
                <ReviewField label="Max Waves" value={maxWaves} />
                <ReviewField label="Runs per Wave" value={runsPerWave} />
                <ReviewField label="Max Total Runs" value={maxWaves * runsPerWave} />
              </div>
            </CardContent>
          </Card>

          {testSetIds.length > 0 && (
            <ReviewBenchmarkTestsCard benchmarkTestSetIds={testSetIds} />
          )}

          {evaluationCriteriaIds.length > 0 && (
            <Card className="border-border">
              <CardContent className="pt-6">
                <p className="text-sm text-muted-foreground font-gc-reg">
                  {evaluationCriteriaIds.length} evaluation criteria selected
                </p>
              </CardContent>
            </Card>
          )}
        </div>
      )}

      <div className="flex items-center justify-between">
        <div>
          {step > 0 && (
            <Button variant="outline" onClick={() => setStep(step - 1)}>
              <ArrowLeftIcon className="w-4 h-4 mr-1" />
              Back
            </Button>
          )}
        </div>

        {step === 0 && (
          <Button onClick={() => setStep(1)} disabled={!canProceedStep0}>
            Next
            <ArrowRightIcon className="w-4 h-4 ml-1" />
          </Button>
        )}

        {step === 1 && (
          <Button onClick={() => setStep(2)}>
            Next
            <ArrowRightIcon className="w-4 h-4 ml-1" />
          </Button>
        )}

        {step === 2 && (
          <Button onClick={handleSubmit} disabled={submitting} size="lg">
            {submitting ? (
              <Loader2Icon className="w-4 h-4 mr-2 animate-spin" />
            ) : (
              <RocketIcon className="w-4 h-4 mr-2" />
            )}
            {submitting ? "Launching..." : "Launch Search"}
          </Button>
        )}
      </div>
    </div>
  );
};

export default NewCompassSearch;
